import { useState, useEffect, useMemo, useCallback, Fragment } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
    Container, Typography, Button, IconButton, Collapse, Box, Card, CardContent, Tooltip, CircularProgress, Checkbox,
    Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, MenuItem, Select,
    OutlinedInput, InputLabel, FormControl, Chip, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
    Pagination, TextField, Avatar, Divider, LinearProgress
} from "@mui/material";
import { Visibility, Edit, Delete, Refresh, VerifiedUser, Cancel, FilterList as FilterListIcon, AssignmentInd as AssignedIcon, TableRows as TableRowsIcon, Language as LanguageIcon, Add as AddIcon } from "@mui/icons-material";
import {
    useReactTable,
    getCoreRowModel,
    getPaginationRowModel,
    getExpandedRowModel,
    flexRender,
} from "@tanstack/react-table";
import { getAllInquiries, getInquiryStats, InquiryUserAssign, InquiryUserRemove, deleteInquiry, addRemark, addReplyToRemark, checkDuplicateInquiries, updateInquiry } from "../../api/inquiryAPI";
import { verifyInquiryLead } from "../../api/verifiedLeadsAPI";
import { getAllUsers } from "../../api/userAPI";
import { useDispatch, useSelector } from "react-redux";
import { setSelectedInquiry } from "../../reduxcomponents/slices/inquirySlice";
import useSnackbar from "../../hooks/useSnackbar";
import usePermissions from "../../hooks/UsePermissions";
import QuickInsertInquiryDialog from "./QuickInsertInquiryDialog";

// ─── Helper ──────────────────────────────────────────────────────────────────
const getLeadSourceStyles = (source, websiteDomain) => {
    const domain = (websiteDomain || "").toLowerCase().trim();
    const src = (source || "").toLowerCase().trim();

    // Specific known website domains
    if (domain.includes("sikkimtourism.in") || src === "sikkimtourism.in" || src.includes("sikkim")) {
        return { color: "#0f766e", bg: "#f0fdfa", border: "#99f6e4", label: "sikkimtourism.in", isWebsite: true };
    }
    if (domain.includes("sandakphutrek.com") || src === "sandakphutrek.com" || src.includes("sandakphu")) {
        return { color: "#b45309", bg: "#fffbeb", border: "#fde68a", label: "sandakphutrek.com", isWebsite: true };
    }
    if (domain.includes("easotrip.com") || src === "easotrip.com" || src === "easotrip" || src === "website") {
        return { color: "#4338ca", bg: "#eef2ff", border: "#c7d2fe", label: "easotrip.com", isWebsite: true };
    }
    if (domain.includes("darjeeling") || src.includes("darjeeling")) {
        return { color: "#0284c7", bg: "#f0f9ff", border: "#bae6fd", label: "darjeelingtourism.com", isWebsite: true };
    }
    if (src === "whatsapp") {
        return { color: "#16a34a", bg: "#f0fdf4", border: "#bbf7d0", label: "WhatsApp", isWebsite: false };
    }
    if (src === "facebook") {
        return { color: "#1d4ed8", bg: "#eff6ff", border: "#bfdbfe", label: "Facebook", isWebsite: false };
    }
    if (src === "instagram") {
        return { color: "#db2777", bg: "#fdf2f8", border: "#fbcfe8", label: "Instagram", isWebsite: false };
    }
    if (src === "offline") {
        return { color: "#4b5563", bg: "#f9fafb", border: "#e5e7eb", label: "Offline", isWebsite: false };
    }
    if (domain && (domain.includes(".com") || domain.includes(".in") || domain.includes(".org") || domain.includes(".net"))) {
        return { color: "#2563eb", bg: "#eff6ff", border: "#bfdbfe", label: domain, isWebsite: true };
    }
    return { color: "#6b7280", bg: "#f3f4f6", border: "#e5e7eb", label: source || "Direct", isWebsite: false };
};

// ─── InquiryTable component ───────────────────────────────────────────────────
const InquiryTable = ({
    tableId, title, headerColor, icon,
    inquiries, loading, page, totalPages, onPageChange, rowsPerPage, onRowsPerPageChange,
    columns, openRow, setOpenRow,
    selectedInquiries, setSelectedInquiries, rowSelection, setRowSelection,
    canReply, canAssignAndRemove, userList,
    handleInquiryClick, handleOpenDeleteDialog, handleOpenVerifyDialog, handleOpenQuickEdit,
    newRemarkText, setNewRemarkText, handleAddRemarkSubmit,
    replyText, setReplyText, activeReplyRemarkId, setActiveReplyRemarkId, handleAddReplySubmit,
    emptyMsg,
    onAssign, onRemove
}) => {
    const table = useReactTable({
        data: inquiries,
        columns,
        getCoreRowModel: getCoreRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        getExpandedRowModel: getExpandedRowModel(),
        initialState: { pagination: { pageSize: inquiries.length > 0 ? inquiries.length : 10, pageIndex: 0 } },
        state: { expanded: openRow ? { [openRow]: true } : {}, rowSelection },
        enableRowSelection: true,
        onRowSelectionChange: setRowSelection,
        onExpandedChange: (updater) => {
            const currentExpanded = openRow ? { [openRow]: true } : {};
            const newExpanded = typeof updater === "function" ? updater(currentExpanded) : updater;
            const expandedKeys = Object.keys(newExpanded).filter(k => newExpanded[k]);
            setOpenRow(expandedKeys.length > 0 ? expandedKeys[expandedKeys.length - 1] : null);
        },
        getRowId: (row) => row._id,
        manualPagination: true,
    });

    return (
        <Paper elevation={3} sx={{ borderRadius: 3, overflow: 'hidden', mb: 3 }}>
            {/* Banner */}
            <Box sx={{ px: 3, py: 2, background: headerColor, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {icon}
                    <Typography variant="h6" fontWeight={700} color="#fff">{title}</Typography>
                    <Chip label={inquiries.length} size="small" sx={{ bgcolor: 'rgba(255,255,255,0.25)', color: '#fff', fontWeight: 700, ml: 0.5 }} />
                </Box>
                {/* Assign / Remove buttons in banner */}
                {(onAssign || onRemove) && canAssignAndRemove && (
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        {onAssign && (
                            <Button
                                variant="contained"
                                size="small"
                                disabled={!selectedInquiries.length}
                                onClick={onAssign}
                                sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: '#fff', borderColor: 'rgba(255,255,255,0.5)', border: '1px solid', backdropFilter: 'blur(4px)', '&:hover': { bgcolor: 'rgba(255,255,255,0.35)' }, '&:disabled': { color: 'rgba(255,255,255,0.4)', borderColor: 'rgba(255,255,255,0.2)' } }}
                            >
                                Assign User
                            </Button>
                        )}
                        {onRemove && (
                            <Button
                                variant="contained"
                                size="small"
                                disabled={!selectedInquiries.length}
                                onClick={onRemove}
                                sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: '#fff', borderColor: 'rgba(255,255,255,0.5)', border: '1px solid', backdropFilter: 'blur(4px)', '&:hover': { bgcolor: 'rgba(255,255,255,0.35)' }, '&:disabled': { color: 'rgba(255,255,255,0.4)', borderColor: 'rgba(255,255,255,0.2)' } }}
                            >
                                Remove User
                            </Button>
                        )}
                    </Box>
                )}
            </Box>

            {loading && <LinearProgress />}

            <TableContainer id={tableId} sx={{ overflowX: 'auto' }}>
                <Table size="small" stickyHeader>
                    <TableHead>
                        {table.getHeaderGroups().map(hg => (
                            <TableRow key={hg.id}>
                                {hg.headers.map(h => (
                                    <TableCell key={h.id} sx={{ backgroundColor: '#f8fafc', fontWeight: 700, color: '#475569', ...(h.id === "select" ? { padding: "checkbox" } : {}) }}>
                                        {h.isPlaceholder ? null : flexRender(h.column.columnDef.header, h.getContext())}
                                    </TableCell>
                                ))}
                            </TableRow>
                        ))}
                    </TableHead>
                    <TableBody>
                        {inquiries.length === 0 && !loading ? (
                            <TableRow>
                                <TableCell colSpan={columns.length} align="center" sx={{ py: 4, color: '#94a3b8' }}>{emptyMsg}</TableCell>
                            </TableRow>
                        ) : table.getRowModel().rows.map(row => {
                            const inquiry = row.original;
                            const isExpanded = row.getIsExpanded();
                            return (
                                <Fragment key={row.id}>
                                    <TableRow hover sx={{ backgroundColor: (inquiry.verified || inquiry.verifyed) ? 'inherit' : '#fff8f8', '&:hover': { bgcolor: '#f0f7ff' } }}>
                                        {row.getVisibleCells().map(cell => (
                                            <TableCell key={cell.id} sx={cell.column.id === "select" ? { padding: "checkbox" } : {}}>
                                                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                    {isExpanded && (
                                        <TableRow>
                                            <TableCell colSpan={row.getVisibleCells().length} sx={{ p: 0 }}>
                                                <Collapse in={isExpanded}>
                                                    <Box margin={2}>
                                                        <Card variant="outlined">
                                                            <CardContent sx={{ padding: 2 }}>
                                                                <Typography variant="body2">
                                                                    <strong>Website:</strong> {(() => {
                                                                        const s = getLeadSourceStyles(inquiry.lead_source, inquiry.website_domain);
                                                                        return s.isWebsite ? (
                                                                            <a href={`https://${s.label}`} target="_blank" rel="noreferrer" style={{ color: '#2563eb', fontWeight: 600, textDecoration: 'none', marginLeft: '4px' }}>
                                                                                🌐 {s.label}
                                                                            </a>
                                                                        ) : <span style={{ marginLeft: '4px' }}>{s.label}</span>;
                                                                    })()}
                                                                </Typography>
                                                                <Typography variant="body2"><strong>Lead Source:</strong> {inquiry.lead_source || "N/A"}</Typography>
                                                                <Typography variant="body2"><strong>Message:</strong> {inquiry.guest_message || "N/A"}</Typography>
                                                                <Typography variant="body2"><strong>Verified:</strong> {inquiry.verified || inquiry.verifyed ? "Yes" : "No"}</Typography>
                                                                <Typography variant="body2">
                                                                    <strong>Arrival:</strong> {inquiry.travel_date || inquiry.arrival_date ? new Date(inquiry.travel_date || inquiry.arrival_date).toLocaleDateString() : "N/A"}
                                                                </Typography>
                                                                <Typography variant="body2" sx={{ mb: 2 }}>
                                                                    <strong>Created:</strong> {inquiry.created_at || inquiry.createdAt ? new Date(inquiry.created_at || inquiry.createdAt).toLocaleString() : "N/A"}
                                                                </Typography>

                                                                <Box sx={{ mt: 2, borderTop: '1px solid #e0e0e0', pt: 2 }}>
                                                                    <Typography variant="subtitle2" fontWeight="bold" gutterBottom sx={{ color: '#1976d2' }}>Remarks Timeline</Typography>
                                                                    {(!inquiry.remarks || inquiry.remarks.length === 0) ? (
                                                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontStyle: 'italic' }}>No remarks yet.</Typography>
                                                                    ) : (
                                                                        <Stack spacing={2} sx={{ mb: 2 }}>
                                                                            {inquiry.remarks.map((remark) => (
                                                                                <Box key={remark._id} sx={{ p: 1.5, bgcolor: '#f5f5f5', borderRadius: 1, borderLeft: '4px solid #1976d2' }}>
                                                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                                                                        <Typography variant="body2" fontWeight="bold">{remark.userName}</Typography>
                                                                                        <Typography variant="caption" color="text.secondary">{new Date(remark.created_at).toLocaleString()}</Typography>
                                                                                    </Box>
                                                                                    <Typography variant="body2" sx={{ mb: 1, whiteSpace: 'pre-wrap' }}>{remark.text}</Typography>
                                                                                    {remark.replies?.length > 0 && (
                                                                                        <Stack spacing={1} sx={{ pl: 2, mt: 1, borderLeft: '1px dashed #ccc' }}>
                                                                                            {remark.replies.map((reply) => (
                                                                                                <Box key={reply._id} sx={{ p: 1, bgcolor: '#fff', borderRadius: 1, border: '1px solid #eaeaea' }}>
                                                                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                                                                                        <Typography variant="caption" fontWeight="bold" color="primary">{reply.userName}</Typography>
                                                                                                        <Typography variant="caption" color="text.secondary">{new Date(reply.created_at).toLocaleString()}</Typography>
                                                                                                    </Box>
                                                                                                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{reply.text}</Typography>
                                                                                                </Box>
                                                                                            ))}
                                                                                        </Stack>
                                                                                    )}
                                                                                    {canReply && (
                                                                                        <Box sx={{ mt: 1 }}>
                                                                                            {activeReplyRemarkId === remark._id ? (
                                                                                                <Box sx={{ display: 'flex', gap: 1, mt: 1, alignItems: 'center' }}>
                                                                                                    <TextField size="small" placeholder="Write a reply..." value={replyText} onChange={(e) => setReplyText(e.target.value)} fullWidth sx={{ bgcolor: '#fff' }} />
                                                                                                    <Button size="small" variant="contained" onClick={() => handleAddReplySubmit(inquiry._id, remark._id)}>Submit</Button>
                                                                                                    <Button size="small" variant="outlined" color="error" onClick={() => { setActiveReplyRemarkId(null); setReplyText(""); }}>Cancel</Button>
                                                                                                </Box>
                                                                                            ) : (
                                                                                                <Button size="small" sx={{ fontSize: '0.75rem', p: 0, textTransform: 'none' }} onClick={() => { setActiveReplyRemarkId(remark._id); setReplyText(""); }}>Reply</Button>
                                                                                            )}
                                                                                        </Box>
                                                                                    )}
                                                                                </Box>
                                                                            ))}
                                                                        </Stack>
                                                                    )}
                                                                    <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                                                                        <TextField size="small" placeholder="Add a remark..." value={newRemarkText} onChange={(e) => setNewRemarkText(e.target.value)} fullWidth sx={{ bgcolor: '#fff' }} />
                                                                        <Button variant="contained" size="small" onClick={() => handleAddRemarkSubmit(inquiry._id)}>Add Remark</Button>
                                                                    </Box>
                                                                </Box>
                                                            </CardContent>
                                                        </Card>
                                                    </Box>
                                                </Collapse>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </Fragment>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Pagination */}
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 3, py: 1.5, borderTop: '1px solid #e2e8f0', flexWrap: "wrap" }}>
                <Pagination count={totalPages} page={page} onChange={onPageChange} color="primary" size="small" showFirstButton showLastButton />
                <FormControl size="small" sx={{ minWidth: 130 }}>
                    <InputLabel>Rows per page</InputLabel>
                    <Select value={rowsPerPage} label="Rows per page" onChange={onRowsPerPageChange}>
                        <MenuItem value={10}>10</MenuItem>
                        <MenuItem value={25}>25</MenuItem>
                        <MenuItem value={50}>50</MenuItem>
                        <MenuItem value={100}>100</MenuItem>
                    </Select>
                </FormControl>
            </Box>
        </Paper>
    );
};

// ─── Main Page ────────────────────────────────────────────────────────────────
const Inquiry = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const { showSnackbar, SnackbarComponent } = useSnackbar();
    const hasPermission = usePermissions();
    const { user } = useSelector((state) => state.tokens);

    const canReply = ["Admin", "Admin-a", "Sales Manager", "Sales Team Lead", "Operation Manager"].includes(user?.permission);
    const canAssignAndRemove = ["Admin", "Admin-a", "Sales Manager", "Operation Manager"].includes(user?.permission);
    const isAdminView = ["Admin", "Admin-a", "Sales Manager", "Sales Team Lead", "Operation Manager"].includes(user?.permission);

    // ── Shared UI state ──
    const [userList, setUserList] = useState([]);
    const [assignedUserFilter, setAssignedUserFilter] = useState(""); // "" = all assigned, userId = specific user
    const [searchQuery, setSearchQuery] = useState(location.state?.search || "");

    useEffect(() => {
        if (location.state?.search) {
            setSearchQuery(location.state.search);
        }
    }, [location.state?.search]);
    const [leadSourceFilter, setLeadSourceFilter] = useState("");
    const [createdStartDate, setCreatedStartDate] = useState("");
    const [createdEndDate, setCreatedEndDate] = useState("");
    const [inquiryStats, setInquiryStats] = useState({ totalAssigned: 0, userCounts: {} });

    // ── Table 1: Assigned inquiries ──
    const [assignedInquiries, setAssignedInquiries] = useState([]);
    const [assignedPage, setAssignedPage] = useState(1);
    const [assignedTotalPages, setAssignedTotalPages] = useState(1);
    const [assignedRowsPerPage, setAssignedRowsPerPage] = useState(25);
    const [assignedLoading, setAssignedLoading] = useState(false);
    const [assignedOpenRow, setAssignedOpenRow] = useState(null);
    const [assignedRowSelection, setAssignedRowSelection] = useState({});

    // ── Table 2: All inquiries ──
    const [allInquiries, setAllInquiries] = useState([]);
    const [allPage, setAllPage] = useState(1);
    const [allTotalPages, setAllTotalPages] = useState(1);
    const [allRowsPerPage, setAllRowsPerPage] = useState(25);
    const [allLoading, setAllLoading] = useState(false);
    const [allOpenRow, setAllOpenRow] = useState(null);
    const [allRowSelection, setAllRowSelection] = useState({});

    // ── Sync row selections to selectedInquiries array ──
    useEffect(() => {
        const assignedIds = Object.keys(assignedRowSelection).filter(k => assignedRowSelection[k]);
        const allIds = Object.keys(allRowSelection).filter(k => allRowSelection[k]);
        const uniqueIds = [...new Set([...assignedIds, ...allIds])];
        setSelectedInquiries(uniqueIds);
    }, [assignedRowSelection, allRowSelection]);

    // ── Shared remark/reply state ──
    const [newRemarkText, setNewRemarkText] = useState("");
    const [activeReplyRemarkId, setActiveReplyRemarkId] = useState(null);
    const [replyText, setReplyText] = useState("");

    // ── Dialog states ──
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [selectedInquiryId, setSelectedInquiryId] = useState(null);
    const [selectedInquiries, setSelectedInquiries] = useState([]);
    const [assignDialogOpen, setAssignDialogOpen] = useState(false);
    const [userRemoveDialogOpen, setUserRemoveDialogOpen] = useState(false);
    const [selectedUserIds, setSelectedUserIds] = useState([]);
    const [verifyDialogOpen, setVerifyDialogOpen] = useState(false);
    const [selectedVerifyInquiry, setSelectedVerifyInquiry] = useState(null);
    const [duplicateInquiries, setDuplicateInquiries] = useState([]);
    const [duplicateCheckResult, setDuplicateCheckResult] = useState(null);
    const [checkingDuplicates, setCheckingDuplicates] = useState(false);
    const [quickEditDialogOpen, setQuickEditDialogOpen] = useState(false);
    const [selectedQuickEditInquiry, setSelectedQuickEditInquiry] = useState(null);
    const [quickEditForm, setQuickEditForm] = useState({ guest_name: "", guest_email: "", guest_phone: "" });
    const [quickInsertOpen, setQuickInsertOpen] = useState(false);

    // ── Fetch helpers ──
    const fetchAssigned = useCallback(async (pageNum = 1, limit = assignedRowsPerPage, userFilter = assignedUserFilter) => {
        setAssignedLoading(true);
        try {
            // For assigned table: Use userFilter if provided
            const finalUser = userFilter || undefined;

            // If no specific user selected for admin, we still need to get "assigned" ones
            // Use a special flag or just fetch with no filter and filter client side
            const response = await getAllInquiries(pageNum, limit, searchQuery, leadSourceFilter, "", "", createdStartDate, createdEndDate, finalUser);
            let data = response.data || [];
            if (!Array.isArray(data)) data = response.data?.inquiries || response.inquiries || [];

            // For admin "all assigned" view (no specific user), filter client-side for those with teams
            let filtered = data;
            if (isAdminView && !userFilter) {
                filtered = data.filter(i => i.manage_teams && i.manage_teams.length > 0);
            }

            setAssignedInquiries(filtered);
            const pagination = response.pagination || response.data?.pagination;
            setAssignedTotalPages(pagination?.totalPages || 1);
        } catch (e) { console.error(e); }
        finally { setAssignedLoading(false); }
    }, [searchQuery, leadSourceFilter, createdStartDate, createdEndDate, assignedRowsPerPage, assignedUserFilter, user, isAdminView]);

    const fetchAll = useCallback(async (pageNum = 1, limit = allRowsPerPage) => {
        if (!isAdminView) return; // Sales Executive only sees assigned table
        setAllLoading(true);
        try {
            const response = await getAllInquiries(pageNum, limit, searchQuery, leadSourceFilter, "", "", createdStartDate, createdEndDate, "");
            let data = response.data || [];
            if (!Array.isArray(data)) data = response.data?.inquiries || response.inquiries || [];
            setAllInquiries(data);
            const pagination = response.pagination || response.data?.pagination;
            setAllTotalPages(pagination?.totalPages || 1);
        } catch (e) { console.error(e); }
        finally { setAllLoading(false); }
    }, [searchQuery, leadSourceFilter, createdStartDate, createdEndDate, allRowsPerPage, isAdminView]);

    const fetchStats = useCallback(async () => {
        if (!isAdminView) return;
        try {
            const res = await getInquiryStats(searchQuery, leadSourceFilter, "", "", createdStartDate, createdEndDate);
            if (res.success && res.data) {
                setInquiryStats(res.data);
            }
        } catch (e) { console.error("Error fetching stats:", e); }
    }, [searchQuery, leadSourceFilter, createdStartDate, createdEndDate, isAdminView]);

    const refreshBoth = useCallback(() => {
        fetchAssigned(assignedPage, assignedRowsPerPage, assignedUserFilter);
        fetchAll(allPage, allRowsPerPage);
        fetchStats();
    }, [fetchAssigned, fetchAll, fetchStats, assignedPage, assignedRowsPerPage, assignedUserFilter, allPage, allRowsPerPage]);

    // Initial load
    useEffect(() => { refreshBoth(); }, []);

    // Re-fetch on filter changes (debounce search)
    useEffect(() => {
        const timer = setTimeout(() => {
            setAssignedPage(1); setAllPage(1);
            fetchAssigned(1, assignedRowsPerPage, assignedUserFilter);
            fetchAll(1, allRowsPerPage);
            fetchStats();
        }, searchQuery ? 500 : 0);
        return () => clearTimeout(timer);
    }, [searchQuery, leadSourceFilter, createdStartDate, createdEndDate]);

    // Re-fetch assigned when chip filter changes
    useEffect(() => {
        setAssignedPage(1);
        fetchAssigned(1, assignedRowsPerPage, assignedUserFilter);
    }, [assignedUserFilter]);

    // Fetch users
    useEffect(() => {
        getAllUsers().then(res => {
            if (res.data) setUserList(res.data.filter(u => u.isActive !== false && ["User", "Operation", "Sales Manager", "Sales Team Lead", "Operation Manager", "Sales Executive"].includes(u.permission)));
        }).catch(console.error);
    }, []);

    const salesExecs = useMemo(() => {
        return userList.filter(u => 
            ["Sales Executive", "Sales Team Lead", "Sales Manager"].includes(u.permission) ||
            (inquiryStats?.userCounts && ((inquiryStats.userCounts[u.id] || inquiryStats.userCounts[u._id] || 0) > 0))
        );
    }, [userList, inquiryStats]);

    // ── Remark handlers ──
    const handleAddRemarkSubmit = async (inquiryId) => {
        if (!newRemarkText.trim()) return;
        try {
            const res = await addRemark(inquiryId, newRemarkText);
            const updateList = (list) => list.map(i => i._id === inquiryId ? { ...i, remarks: res.data?.remarks || i.remarks } : i);
            setAssignedInquiries(prev => updateList(prev));
            setAllInquiries(prev => updateList(prev));
            setNewRemarkText("");
            showSnackbar("Remark added", "success");
        } catch (err) { showSnackbar(err.response?.data?.message || "Failed", "error"); }
    };

    const handleAddReplySubmit = async (inquiryId, remarkId) => {
        if (!replyText.trim()) return;
        try {
            const res = await addReplyToRemark(inquiryId, remarkId, replyText);
            const updateList = (list) => list.map(i => i._id === inquiryId ? { ...i, remarks: res.data?.remarks || i.remarks } : i);
            setAssignedInquiries(prev => updateList(prev));
            setAllInquiries(prev => updateList(prev));
            setReplyText(""); setActiveReplyRemarkId(null);
            showSnackbar("Reply added", "success");
        } catch (err) { showSnackbar(err.response?.data?.message || "Failed", "error"); }
    };

    // ── Other handlers ──
    const handleOpenVerifyDialog = useCallback(async (inquiry) => {
        setSelectedVerifyInquiry(inquiry);
        setDuplicateInquiries([]);
        setDuplicateCheckResult(null);
        setVerifyDialogOpen(true);
        const phone = inquiry.guest_phone || inquiry.phone;
        const email = inquiry.guest_email || inquiry.email;
        if (phone || email) {
            setCheckingDuplicates(true);
            try {
                const res = await checkDuplicateInquiries(
                    email,
                    phone,
                    inquiry._id,
                    inquiry.destination || inquiry.location || ""
                );
                if (res.success) {
                    setDuplicateCheckResult(res);
                    if (res.duplicates) setDuplicateInquiries(res.duplicates);
                }
            } catch (e) {
                console.error("Duplicate check error:", e);
            } finally {
                setCheckingDuplicates(false);
            }
        }
    }, []);

    const handleInquiryClick = useCallback((inquiry) => {
        const isVerified = Boolean(inquiry.verified || inquiry.verifyed);
        if (!isVerified) {
            showSnackbar("Please verify this inquiry first before generating a quotation.", "warning");
            handleOpenVerifyDialog(inquiry);
            return;
        }
        dispatch(setSelectedInquiry(inquiry));
        navigate("/createItinerary");
    }, [dispatch, navigate, showSnackbar, handleOpenVerifyDialog]);

    const handleOpenDeleteDialog = useCallback((id) => { setSelectedInquiryId(id); setDeleteDialogOpen(true); }, []);
    const handleCloseDeleteDialog = () => { setDeleteDialogOpen(false); setSelectedInquiryId(null); };
    const handleConfirmDelete = async () => {
        try { await deleteInquiry(selectedInquiryId); showSnackbar("Deleted", "success"); refreshBoth(); }
        catch (e) { showSnackbar("Failed to delete", "error"); }
        finally { handleCloseDeleteDialog(); }
    };

    const handleCloseVerifyDialog = () => {
        setVerifyDialogOpen(false);
        setSelectedVerifyInquiry(null);
        setDuplicateCheckResult(null);
        setDuplicateInquiries([]);
    };

    const handleConfirmVerify = async (andGenerateQuote = false) => {
        if (!selectedVerifyInquiry) return;
        try {
            const res = await verifyInquiryLead(selectedVerifyInquiry._id);
            showSnackbar(res.message || "Inquiry verified successfully", "success");
            refreshBoth();
            const targetInquiry = { ...selectedVerifyInquiry, verified: true };
            handleCloseVerifyDialog();
            if (andGenerateQuote) {
                dispatch(setSelectedInquiry(targetInquiry));
                navigate("/createItinerary");
            }
        } catch (e) {
            showSnackbar(typeof e === 'string' ? e : (e.message || "Failed to verify"), "error");
        }
    };

    const handleOpenQuickEdit = useCallback((inquiry) => {
        setSelectedQuickEditInquiry(inquiry);
        setQuickEditForm({
            guest_name: inquiry.guest_name || inquiry.name || "",
            guest_email: inquiry.guest_email || inquiry.email || "",
            guest_phone: inquiry.guest_phone || inquiry.phone || "",
            pax: inquiry.pax ?? "",
            kids_above_5: inquiry.kids_above_5 ?? ""
        });
        setQuickEditDialogOpen(true);
    }, []);
    const handleCloseQuickEdit = () => { setQuickEditDialogOpen(false); setSelectedQuickEditInquiry(null); };
    const handleQuickEditSave = async () => {
        try {
            const payload = {
                ...quickEditForm,
                pax: quickEditForm.pax !== '' ? Number(quickEditForm.pax) : undefined,
                kids_above_5: quickEditForm.kids_above_5 !== '' ? Number(quickEditForm.kids_above_5) : 0,
            };
            await updateInquiry(selectedQuickEditInquiry._id, payload);
            showSnackbar("Updated", "success");
            refreshBoth();
        } catch (e) {
            showSnackbar(e.message || "Failed", "error");
        } finally {
            handleCloseQuickEdit();
        }
    };

    const handleAssignUser = async () => {
        try {
            const res = await InquiryUserAssign({ inquiryIds: selectedInquiries, userIds: selectedUserIds });
            setSelectedInquiries([]); setSelectedUserIds([]); setAssignDialogOpen(false);
            setAssignedRowSelection({}); setAllRowSelection({});
            refreshBoth(); showSnackbar(res.message, "success");
        } catch (e) { console.error(e); }
    };
    const handleRemoveUser = async () => {
        try {
            await InquiryUserRemove({ inquiryIds: selectedInquiries, userIds: selectedUserIds });
            setSelectedInquiries([]); setSelectedUserIds([]); setUserRemoveDialogOpen(false);
            setAssignedRowSelection({}); setAllRowSelection({});
            refreshBoth();
        } catch (e) { console.error(e); }
    };

    // Assigned/removable users for dialogs
    const assignedUserIds = selectedInquiries.flatMap(id => [...assignedInquiries, ...allInquiries].find(i => i._id === id)?.manage_teams || []).map(t => t.user_id);
    const uniqueAssignedUserIds = [...new Set(assignedUserIds)];
    const assignableUsers = userList.filter(u => !uniqueAssignedUserIds.includes(u.id));
    const assignedUsersPerInquiry = selectedInquiries.map(id => ([...assignedInquiries, ...allInquiries].find(i => i._id === id)?.manage_teams || []).map(t => t.user_id));
    const removableUserIds = assignedUsersPerInquiry.reduce((a, b) => a.filter(id => b.includes(id)), assignedUsersPerInquiry[0] || []);
    const removableUsers = userList.filter(u => removableUserIds.includes(u.id));

    const getAssignedUserNames = useCallback((inquiry) => {
        if (!inquiry.manage_teams?.length) return "-";
        return inquiry.manage_teams.map(t => {
            if (t.user_id && typeof t.user_id === 'object' && t.user_id.fullName) return t.user_id.fullName;
            const uid = (t.user_id && typeof t.user_id === 'object') ? (t.user_id._id || t.user_id.id) : t.user_id;
            return userList.find(u => u.id === uid || u._id === uid)?.fullName || "Unknown";
        }).join(", ");
    }, [userList]);

    // ── Columns ──
    const columns = useMemo(() => {
        const cols = [];
        if (hasPermission("inquiry", "alter")) {
            cols.push({
                id: "select",
                header: ({ table }) => <Checkbox size="small" checked={table.getIsAllRowsSelected()} indeterminate={table.getIsSomeRowsSelected()} onChange={table.getToggleAllRowsSelectedHandler()} />,
                cell: ({ row }) => <Checkbox size="small" checked={row.getIsSelected()} onChange={row.getToggleSelectedHandler()} />,
                enableSorting: false,
            });
        }
        cols.push(
            { accessorKey: "guest_name", header: "Name" },
            { accessorKey: "guest_email", header: "Email" },
            { accessorKey: "guest_phone", header: "Phone" },
            {
                accessorKey: "lead_source", header: "Lead Source",
                cell: ({ row }) => {
                    const s = getLeadSourceStyles(row.original.lead_source, row.original.website_domain);
                    return (
                        <Tooltip title={s.isWebsite ? `Website: https://${s.label}` : `Source: ${s.label}`}>
                            <Chip
                                icon={s.isWebsite ? <LanguageIcon sx={{ fontSize: '13px !important', color: `${s.color} !important` }} /> : undefined}
                                label={s.label}
                                size="small"
                                sx={{
                                    height: 24,
                                    fontSize: '0.75rem',
                                    fontWeight: 600,
                                    color: s.color,
                                    bgcolor: s.bg,
                                    border: `1px solid ${s.border}`,
                                    borderRadius: '6px',
                                    cursor: s.isWebsite ? 'pointer' : 'default',
                                    '& .MuiChip-label': { px: 0.8 },
                                    '& .MuiChip-icon': { ml: 0.5 }
                                }}
                                onClick={s.isWebsite ? (e) => {
                                    e.stopPropagation();
                                    window.open(`https://${s.label}`, '_blank');
                                } : undefined}
                            />
                        </Tooltip>
                    );
                }
            },
            {
                accessorKey: "travel_date", header: "Arrival Date",
                cell: ({ row }) => { const d = row.original.travel_date || row.original.arrival_date; return d ? new Date(d).toLocaleDateString() : "N/A"; }
            },
            {
                accessorKey: "created_at", header: "Created At",
                cell: ({ row }) => {
                    const d = row.original.created_at || row.original.createdAt;
                    return <Typography variant="body2" sx={{ fontSize: '0.8rem', color: 'text.secondary', whiteSpace: 'nowrap' }}>{d ? new Date(d).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true }) : "N/A"}</Typography>;
                }
            }
        );
        if (hasPermission("inquiry", "alter")) {
            cols.push({ accessorKey: "assigned_users", header: "Assigned Users", cell: ({ row }) => getAssignedUserNames(row.original) });
        }
        cols.push(
            {
                id: "quote",
                header: "Quote",
                cell: ({ row }) => {
                    const inquiry = row.original;
                    const isVerified = Boolean(inquiry.verified || inquiry.verifyed);
                    return (
                        <Tooltip title={isVerified ? "Generate Quotation" : "Inquiry must be verified before quote creation"}>
                            <Button
                                size="small"
                                variant={isVerified ? "contained" : "outlined"}
                                color={isVerified ? "primary" : "warning"}
                                onClick={() => handleInquiryClick(inquiry)}
                                sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.72rem', py: 0.3 }}
                            >
                                {isVerified ? "Generate" : "Verify & Quote"}
                            </Button>
                        </Tooltip>
                    );
                },
                enableSorting: false
            },
            {
                id: "actions", header: "Actions",
                cell: ({ row }) => {
                    const inquiry = row.original;
                    return (
                        <>
                            <Tooltip title="View"><IconButton onClick={() => row.toggleExpanded()} size="small"><Visibility fontSize="small" /></IconButton></Tooltip>
                            {hasPermission("inquiry", "quick-edit") && <Tooltip title="Quick Edit"><IconButton size="small" onClick={() => handleOpenQuickEdit(inquiry)}><Edit fontSize="small" /></IconButton></Tooltip>}
                            {hasPermission("inquiry", "alter") && <Tooltip title="Delete"><IconButton onClick={() => handleOpenDeleteDialog(inquiry._id)} size="small"><Delete fontSize="small" /></IconButton></Tooltip>}
                            {(hasPermission("inquiry", "alter") || hasPermission("inquiry", "verify")) && (
                                (!inquiry.verified && !inquiry.verifyed)
                                    ? <Tooltip title="Verify Lead"><IconButton onClick={() => handleOpenVerifyDialog(inquiry)} size="small" color="error"><Cancel fontSize="small" /></IconButton></Tooltip>
                                    : <Tooltip title="Verified"><IconButton size="small" color="success" disableRipple sx={{ cursor: 'default' }}><VerifiedUser fontSize="small" /></IconButton></Tooltip>
                            )}
                        </>
                    );
                },
                enableSorting: false,
            }
        );
        return cols;
    }, [hasPermission, handleInquiryClick, handleOpenDeleteDialog, getAssignedUserNames]);

    const sharedTableProps = {
        columns, canReply, canAssignAndRemove, userList, handleInquiryClick, handleOpenDeleteDialog, handleOpenVerifyDialog, handleOpenQuickEdit,
        newRemarkText, setNewRemarkText, handleAddRemarkSubmit, replyText, setReplyText, activeReplyRemarkId, setActiveReplyRemarkId, handleAddReplySubmit,
    };

    return (
        <Container maxWidth={false} sx={{ py: 3 }}>

            {/* ── Header / Filter bar ── */}
            <Stack direction="row" alignItems="center" spacing={2} justifyContent="space-between" sx={{ mb: 2, flexWrap: 'wrap' }}>
                <Box>
                    <Typography variant="h4" color="warning" fontWeight={800}>Inquiry List</Typography>
                    <Typography variant="body2" color="text.secondary">Assigned · All data · Filter by assignee</Typography>
                </Box>
                <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center" sx={{ rowGap: 1.5 }}>
                    <Tooltip title="Refresh both tables">
                        <IconButton onClick={refreshBoth} sx={{ bgcolor: "#fff", border: "1px solid #ddd" }}>
                            {(assignedLoading || allLoading) ? <CircularProgress size={20} /> : <Refresh fontSize="small" />}
                        </IconButton>
                    </Tooltip>
                    <OutlinedInput size="small" placeholder="Search Name, Email, Phone..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} sx={{ minWidth: "180px", bgcolor: "#fff" }} />
                    <FormControl size="small" sx={{ minWidth: "170px" }}>
                        <InputLabel>Website / Source</InputLabel>
                        <Select label="Website / Source" value={leadSourceFilter} onChange={(e) => setLeadSourceFilter(e.target.value)} sx={{ bgcolor: "#fff" }}>
                            <MenuItem value="">All Sources & Websites</MenuItem>
                            <MenuItem value="sikkimtourism.in">🌐 sikkimtourism.in</MenuItem>
                            <MenuItem value="sandakphutrek.com">🌐 sandakphutrek.com</MenuItem>
                            <MenuItem value="easotrip.com">🌐 easotrip.com</MenuItem>
                            <MenuItem value="whatsapp">💬 WhatsApp</MenuItem>
                            <MenuItem value="offline">🏢 Offline</MenuItem>
                            <MenuItem value="facebook">Facebook</MenuItem>
                            <MenuItem value="instagram">Instagram</MenuItem>
                        </Select>
                    </FormControl>
                    <TextField size="small" type="date" label="From" value={createdStartDate} onChange={(e) => setCreatedStartDate(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ bgcolor: "#fff", width: "130px" }} />
                    <TextField size="small" type="date" label="To" value={createdEndDate} onChange={(e) => setCreatedEndDate(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ bgcolor: "#fff", width: "130px" }} />
                    {(searchQuery || leadSourceFilter || createdStartDate || createdEndDate) && (
                        <Button size="small" color="error" variant="outlined" onClick={() => { setSearchQuery(""); setLeadSourceFilter(""); setCreatedStartDate(""); setCreatedEndDate(""); }} sx={{ height: '32px' }}>Reset</Button>
                    )}
                    <Box sx={{ flexGrow: 1 }} />
                    <Button
                        variant="contained"
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={() => setQuickInsertOpen(true)}
                        sx={{
                            fontWeight: 700,
                            borderRadius: 1.5,
                            textTransform: 'none',
                            px: 1.8,
                            background: 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)',
                            boxShadow: '0 2px 8px rgba(234, 88, 12, 0.25)',
                            '&:hover': {
                                background: 'linear-gradient(135deg, #ea580c 0%, #c2410c 100%)',
                            }
                        }}
                    >
                        Quick Insert Inquiry
                    </Button>
                    <Button variant="contained" size="small" onClick={() => { dispatch(setSelectedInquiry({})); navigate("/createItinerary"); }}>Create New</Button>
                    {canAssignAndRemove && (
                        <>
                            <Button variant="outlined" size="small" disabled={!selectedInquiries.length} onClick={() => setAssignDialogOpen(true)}>Assign User</Button>
                            <Button variant="outlined" size="small" disabled={!selectedInquiries.length} onClick={() => setUserRemoveDialogOpen(true)}>Remove User</Button>
                        </>
                    )}
                </Stack>
            </Stack>

            {/* ── Assignee Filter Chips ── */}
            {isAdminView && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3, p: 1.5, bgcolor: '#f8fafc', borderRadius: 2, border: '1px solid #e2e8f0', alignItems: 'center' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mr: 1 }}>
                        <FilterListIcon fontSize="small" sx={{ color: '#64748b' }} />
                        <Typography variant="caption" fontWeight={700} color="#64748b" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>Filter Assigned Table</Typography>
                    </Box>
                    <Chip label={`All Assigned (${inquiryStats.totalAssigned || 0})`} onClick={() => setAssignedUserFilter("")} variant={assignedUserFilter === "" ? 'filled' : 'outlined'} color={assignedUserFilter === "" ? 'primary' : 'default'} sx={{ fontWeight: 600, cursor: 'pointer' }} />
                    {salesExecs.map(u => {
                        const uid = u.id || u._id;
                        const isActive = assignedUserFilter === uid;
                        const count = (inquiryStats.userCounts && (inquiryStats.userCounts[uid] || inquiryStats.userCounts[u.id] || inquiryStats.userCounts[u._id])) || 0;
                        return (
                            <Chip key={uid}
                                avatar={<Avatar sx={{ bgcolor: isActive ? '#fff' : '#e2e8f0', color: isActive ? 'primary.main' : '#475569', fontWeight: 700, fontSize: 12 }}>{u.fullName?.charAt(0)}</Avatar>}
                                label={`${u.fullName} (${count})`}
                                onClick={() => setAssignedUserFilter(isActive ? "" : uid)}
                                variant={isActive ? 'filled' : 'outlined'}
                                color={isActive ? 'primary' : 'default'}
                                sx={{ fontWeight: 600, cursor: 'pointer' }}
                            />
                        );
                    })}
                </Box>
            )}

            {/* ── TABLE 1: Assigned Inquiries (top) ── */}
            <InquiryTable
                tableId="assigned-table"
                title={assignedUserFilter ? `Assigned to: ${salesExecs.find(u => (u.id || u._id) === assignedUserFilter)?.fullName || "User"}` : "Assigned Inquiries"}
                headerColor="linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)"
                icon={<AssignedIcon sx={{ color: '#fff' }} />}
                inquiries={assignedInquiries}
                loading={assignedLoading}
                page={assignedPage}
                totalPages={assignedTotalPages}
                onPageChange={(e, v) => { setAssignedPage(v); fetchAssigned(v, assignedRowsPerPage, assignedUserFilter); }}
                rowsPerPage={assignedRowsPerPage}
                onRowsPerPageChange={(e) => { setAssignedRowsPerPage(e.target.value); setAssignedPage(1); fetchAssigned(1, e.target.value, assignedUserFilter); }}
                openRow={assignedOpenRow} setOpenRow={setAssignedOpenRow}
                selectedInquiries={selectedInquiries} setSelectedInquiries={setSelectedInquiries}
                rowSelection={assignedRowSelection} setRowSelection={setAssignedRowSelection}
                emptyMsg={assignedUserFilter ? "No inquiries assigned to this user." : "No assigned inquiries found."}
                {...sharedTableProps}
            />

            {/* ── TABLE 2: All Inquiries (bottom) — Admin/Sales Manager only ── */}
            {isAdminView && (
                <InquiryTable
                    tableId="all-table"
                    title="All Inquiries"
                    headerColor="linear-gradient(135deg, #0f766e 0%, #14b8a6 100%)"
                    icon={<TableRowsIcon sx={{ color: '#fff' }} />}
                    inquiries={allInquiries}
                    loading={allLoading}
                    page={allPage}
                    totalPages={allTotalPages}
                    onPageChange={(e, v) => { setAllPage(v); fetchAll(v, allRowsPerPage); }}
                    rowsPerPage={allRowsPerPage}
                    onRowsPerPageChange={(e) => { setAllRowsPerPage(e.target.value); setAllPage(1); fetchAll(1, e.target.value); }}
                    openRow={allOpenRow} setOpenRow={setAllOpenRow}
                    selectedInquiries={selectedInquiries} setSelectedInquiries={setSelectedInquiries}
                    rowSelection={allRowSelection} setRowSelection={setAllRowSelection}
                    emptyMsg="No inquiries found."
                    onAssign={() => setAssignDialogOpen(true)}
                    onRemove={() => setUserRemoveDialogOpen(true)}
                    {...sharedTableProps}
                />
            )}

            {/* ── Dialogs ── */}
            <Dialog open={assignDialogOpen} onClose={() => setAssignDialogOpen(false)}>
                <DialogTitle>Assign Users</DialogTitle>
                <DialogContent>
                    <DialogContentText>Select users to assign to selected inquiries.</DialogContentText>
                    <FormControl fullWidth sx={{ mt: 2 }}>
                        <InputLabel>Select Users</InputLabel>
                        <Select multiple value={selectedUserIds} onChange={(e) => setSelectedUserIds(e.target.value)} input={<OutlinedInput label="Select Users" />}
                            renderValue={(selected) => <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>{selected.map(v => <Chip key={v} label={userList.find(u => u.id === v)?.fullName || v} />)}</Box>}>
                            {assignableUsers.map(u => <MenuItem key={u.id} value={u.id}>{u.fullName} ({u.email})</MenuItem>)}
                        </Select>
                    </FormControl>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setAssignDialogOpen(false)}>Cancel</Button>
                    <Button onClick={handleAssignUser} color="primary">Assign</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={userRemoveDialogOpen} onClose={() => setUserRemoveDialogOpen(false)}>
                <DialogTitle>Remove Users</DialogTitle>
                <DialogContent>
                    <DialogContentText>Select users to remove from selected inquiries.</DialogContentText>
                    <FormControl fullWidth sx={{ mt: 2 }}>
                        <InputLabel>Select Users</InputLabel>
                        <Select multiple value={selectedUserIds} onChange={(e) => setSelectedUserIds(e.target.value)} input={<OutlinedInput label="Select Users" />}
                            renderValue={(selected) => <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>{selected.map(v => <Chip key={v} label={userList.find(u => u.id === v)?.fullName || v} />)}</Box>}>
                            {removableUsers.map(u => <MenuItem key={u.id} value={u.id}>{u.fullName} ({u.email})</MenuItem>)}
                        </Select>
                    </FormControl>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setUserRemoveDialogOpen(false)}>Cancel</Button>
                    <Button onClick={handleRemoveUser} sx={{ color: 'red' }}>Remove</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={deleteDialogOpen} onClose={handleCloseDeleteDialog}>
                <DialogTitle>Confirm Delete</DialogTitle>
                <DialogContent><DialogContentText>Are you sure you want to delete this inquiry?</DialogContentText></DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDeleteDialog}>Cancel</Button>
                    <Button onClick={handleConfirmDelete} color="error" variant="contained">Delete</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={verifyDialogOpen} onClose={handleCloseVerifyDialog} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ fontWeight: 800 }}>Verify Inquiry to Lead</DialogTitle>
                <DialogContent dividers>
                    <DialogContentText sx={{ mb: 2 }}>
                        Verify this inquiry to move it to Verified Leads and unlock quotation creation.
                    </DialogContentText>
                    {selectedVerifyInquiry && (
                        <Box sx={{ bgcolor: '#f8fafc', p: 2, borderRadius: 2, border: '1px solid #e2e8f0', mb: 2 }}>
                            <Stack spacing={0.6}>
                                <Typography variant="body2"><strong>Guest Name:</strong> {selectedVerifyInquiry.guest_name || selectedVerifyInquiry.name}</Typography>
                                <Typography variant="body2"><strong>Phone Number:</strong> {selectedVerifyInquiry.guest_country_code || ''} {selectedVerifyInquiry.guest_phone || selectedVerifyInquiry.phone || 'N/A'}</Typography>
                                <Typography variant="body2"><strong>Email:</strong> {selectedVerifyInquiry.guest_email || selectedVerifyInquiry.email || 'N/A'}</Typography>
                                <Typography variant="body2"><strong>Destination:</strong> {selectedVerifyInquiry.destination || 'N/A'}</Typography>
                                <Typography variant="body2"><strong>Pax:</strong> {selectedVerifyInquiry.pax || 0}{selectedVerifyInquiry.kids_above_5 ? ` | Children (5-11 yr): ${selectedVerifyInquiry.kids_above_5}` : ''}</Typography>
                            </Stack>
                        </Box>
                    )}

                    {checkingDuplicates && (
                        <Box py={2} display="flex" alignItems="center" justifyContent="center" gap={1.5}>
                            <CircularProgress size={22} />
                            <Typography variant="body2" color="text.secondary">Checking phone number history across all trips and inquiries...</Typography>
                        </Box>
                    )}

                    {!checkingDuplicates && duplicateCheckResult && (
                        <Box sx={{ mb: 2 }}>
                            {/* Loyalty / Duplicate / Multiple Status Banner */}
                            {duplicateCheckResult.isLoyalGuest && (
                                <Box sx={{ p: 1.5, mb: 1.5, borderRadius: 1.5, bgcolor: '#f0fdf4', border: '1px solid #86efac', display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Chip label="⭐ Loyal Guest" color="success" size="small" sx={{ fontWeight: 800 }} />
                                    <Typography variant="body2" sx={{ color: '#166534', fontWeight: 600 }}>
                                        Recognized Loyal Guest with {duplicateCheckResult.confirmedTripsCount || 1} confirmed trip(s).
                                    </Typography>
                                </Box>
                            )}
                            {!duplicateCheckResult.isLoyalGuest && duplicateCheckResult.isDuplicate && (
                                <Box sx={{ p: 1.5, mb: 1.5, borderRadius: 1.5, bgcolor: '#fff7ed', border: '1px solid #fdba74', display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Chip label="⚠️ Duplicate Inquiry" color="warning" size="small" sx={{ fontWeight: 800 }} />
                                    <Typography variant="body2" sx={{ color: '#9a3412', fontWeight: 600 }}>
                                        Guest previously inquired for the same destination ({selectedVerifyInquiry?.destination || 'same destination'}).
                                    </Typography>
                                </Box>
                            )}
                            {!duplicateCheckResult.isLoyalGuest && duplicateCheckResult.isMultiple && (
                                <Box sx={{ p: 1.5, mb: 1.5, borderRadius: 1.5, bgcolor: '#eff6ff', border: '1px solid #93c5fd', display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Chip label="🔁 Multiple Inquiries" color="info" size="small" sx={{ fontWeight: 800 }} />
                                    <Typography variant="body2" sx={{ color: '#1e40af', fontWeight: 600 }}>
                                        Guest has inquired for multiple destinations/trips before.
                                    </Typography>
                                </Box>
                            )}
                            {!duplicateCheckResult.isLoyalGuest && !duplicateCheckResult.isDuplicate && !duplicateCheckResult.isMultiple && (
                                <Box sx={{ p: 1.2, mb: 1.5, borderRadius: 1.5, bgcolor: '#f8fafc', border: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Chip label="✨ New Guest" size="small" sx={{ fontWeight: 700 }} />
                                    <Typography variant="body2" color="text.secondary">
                                        No prior inquiry history found for this phone number.
                                    </Typography>
                                </Box>
                            )}

                            {duplicateInquiries.length > 0 && (
                                <Box sx={{ bgcolor: '#fff', p: 1.5, borderRadius: 1.5, border: '1px solid #e2e8f0' }}>
                                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700, color: '#334155' }}>
                                        Previous Record History ({duplicateInquiries.length})
                                    </Typography>
                                    <Stack spacing={1} sx={{ maxHeight: 200, overflowY: 'auto' }}>
                                        {duplicateInquiries.map((dup, idx) => (
                                            <Box key={dup.id || idx} sx={{ p: 1, bgcolor: '#f8fafc', borderRadius: 1, border: '1px solid #e2e8f0' }}>
                                                <Stack direction="row" justifyContent="space-between" alignItems="center">
                                                    <Typography variant="body2" fontWeight={700} color="primary.main">
                                                        {dup.destination || 'General Inquiry'}
                                                    </Typography>
                                                    <Chip label={dup.source || dup.status || 'Past Lead'} size="small" sx={{ height: 20, fontSize: '0.68rem', fontWeight: 600 }} />
                                                </Stack>
                                                <Stack direction="row" spacing={2} sx={{ mt: 0.5 }}>
                                                    {dup.quotation_no && <Typography variant="caption"><strong>Quote #:</strong> {dup.quotation_no}</Typography>}
                                                    {dup.stage && <Typography variant="caption"><strong>Stage:</strong> {dup.stage}</Typography>}
                                                    <Typography variant="caption" color="text.secondary">
                                                        {dup.date ? new Date(dup.date).toLocaleDateString() : 'N/A'}
                                                    </Typography>
                                                </Stack>
                                            </Box>
                                        ))}
                                    </Stack>
                                </Box>
                            )}
                        </Box>
                    )}
                </DialogContent>
                <DialogActions sx={{ p: 2, gap: 1 }}>
                    <Button onClick={handleCloseVerifyDialog} color="inherit">Cancel</Button>
                    <Button onClick={() => handleConfirmVerify(false)} color="primary" variant="outlined">
                        Confirm Verification
                    </Button>
                    <Button onClick={() => handleConfirmVerify(true)} color="primary" variant="contained" sx={{ fontWeight: 700 }}>
                        Verify & Generate Quote Now
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={quickEditDialogOpen} onClose={handleCloseQuickEdit}>
                <DialogTitle>Quick Edit Inquiry</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1, minWidth: 300 }}>
                        <TextField label="Name" size="small" fullWidth value={quickEditForm.guest_name} onChange={(e) => setQuickEditForm({ ...quickEditForm, guest_name: e.target.value })} />
                        <TextField label="Email" size="small" fullWidth value={quickEditForm.guest_email} onChange={(e) => setQuickEditForm({ ...quickEditForm, guest_email: e.target.value })} />
                        <TextField label="Phone" size="small" fullWidth value={quickEditForm.guest_phone} onChange={(e) => setQuickEditForm({ ...quickEditForm, guest_phone: e.target.value })} />
                        <Stack direction="row" spacing={2}>
                            <TextField label="Pax" type="number" size="small" fullWidth value={quickEditForm.pax ?? ''} onChange={(e) => setQuickEditForm({ ...quickEditForm, pax: e.target.value })} inputProps={{ min: 1 }} />
                            <TextField label="Children (Age 5 to 11 yr)" type="number" size="small" fullWidth value={quickEditForm.kids_above_5 ?? ''} onChange={(e) => setQuickEditForm({ ...quickEditForm, kids_above_5: e.target.value })} inputProps={{ min: 0 }} />
                        </Stack>
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseQuickEdit} color="inherit">Cancel</Button>
                    <Button onClick={handleQuickEditSave} color="primary" variant="contained">Save Changes</Button>
                </DialogActions>
            </Dialog>

            <QuickInsertInquiryDialog
                open={quickInsertOpen}
                onClose={() => setQuickInsertOpen(false)}
                onSuccess={refreshBoth}
                showSnackbar={showSnackbar}
            />

            <SnackbarComponent />
        </Container>
    );
};

export default Inquiry;
